import { Button } from '@/components/ui/button';
import { useUploadImage } from '@/features/upload/api/upload.hook';
import { Loader2, Paperclip, X } from 'lucide-react';
import { useRef } from 'react';
import { toast } from 'sonner';

export type WebsitePhoto = { key: string; name: string; url: string };
export function SiteImageAttachments({
  photos,
  onChange,
  uploading,
  onUploading,
  disabled,
}: {
  photos: WebsitePhoto[];
  onChange: (photos: WebsitePhoto[]) => void;
  uploading: boolean;
  onUploading: (value: boolean) => void;
  disabled: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const upload = useUploadImage({ purpose: 'profile', showToast: false });
  async function addPhotos(files: File[]) {
    if (disabled || uploading) return;
    if (
      files.length + photos.length > 8 ||
      files.some(
        (file) =>
          !['image/jpeg', 'image/png', 'image/webp'].includes(file.type) ||
          file.size > 10 * 1024 * 1024
      )
    ) {
      toast.error('Choose up to 8 JPEG, PNG or WebP photos, each under 10 MB.');
      return;
    }
    onUploading(true);
    const added = [...photos];
    try {
      for (const file of files) {
        const result = await upload.uploadAsync(file);
        if (!result.isPublic || !result.url.startsWith('https://'))
          throw new Error('This photo could not be added.');
        added.push({
          key: result.key,
          name: file.name.slice(0, 200),
          url: result.url,
        });
        onChange([...added]);
      }
    } catch {
      toast.error(
        'Some photos could not be uploaded. Your completed uploads are kept.'
      );
    } finally {
      onUploading(false);
    }
  }
  return (
    <div className="space-y-2">
      {photos.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {photos.map((photo) => (
            <div key={photo.key} className="relative rounded-lg border p-1">
              <img
                src={photo.url}
                alt={photo.name}
                className="size-12 rounded-md object-cover"
              />
              <Button
                type="button"
                variant="secondary"
                size="icon"
                className="absolute -top-1 -right-1 size-5 rounded-full"
                aria-label={`Remove ${photo.name}`}
                disabled={disabled || uploading}
                onClick={() =>
                  onChange(photos.filter((item) => item.key !== photo.key))
                }
              >
                <X className="size-3" />
              </Button>
            </div>
          ))}
        </div>
      )}
      <input
        ref={input}
        type="file"
        multiple
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        aria-label="Upload website photos"
        disabled={disabled || uploading}
        onChange={(event) => {
          const files = Array.from(event.currentTarget.files || []);
          event.currentTarget.value = '';
          void addPhotos(files);
        }}
      />
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs text-stone-500"
          title="Upload photos for your public website"
          disabled={disabled || uploading || photos.length >= 8}
          onClick={() => input.current?.click()}
        >
          {uploading ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <Paperclip className="size-3.5" />
          )}{' '}
          {uploading ? 'Uploading…' : 'Add photos'}
        </Button>
        {photos.length > 0 && (
          <span className="text-[10px] text-stone-400">
            Public website photos
          </span>
        )}
      </div>
    </div>
  );
}
